import React from 'react';
import {useSelector} from 'react-redux';
import {Redirect, Route, useLocation} from 'react-router-dom';
import RestrictedArea from 'components/layout/RestrictedArea';
import Dashboard from 'routes/App/dashboard';
import ReportsContainer from 'routes/App/reports';
import Settings from 'routes/App/settings';
import VirtualNumber from 'routes/App/virtualNumber';
import MerchantsRoutes from 'routes/App/merchants';
import TeamContainer from 'routes/App/usersManagement/TeamContainer';
import CreateLanding from 'routes/App/account/MyAccount/createLanding';
import BankAccount from 'routes/App/account/MyAccount/bankAccount';
import TeamMembers from 'routes/App/account/MyAccount/teamMembers';
import GoogleMyBusiness from 'routes/App/googleMyBusiness/GoogleMyBusiness';
import AddLink from 'routes/App/account/addLink';
import AccountRoutes from 'routes/App/account';
import CustomerInsights from 'routes/App/customerInsights';
import Discounts from 'routes/App/discounts';
import DiscountsV2 from 'routes/App/discountsV2';
import BookADriverRoutes from 'routes/App/BookADriver';
import {
  FEATURE_RESTRICTION_IDS,
  FEATURE_RESTRICTION_PATHS,
  getBookADriverRestriction,
  getInsightsRestriction,
  checkIfSauceEmployee,
  hasBookADriverAccess
} from 'helpers/constants/restrictions';
import FeedbackRoutes from 'routes/App/feedback';
import NoMatchPage from 'routes/App/noMatchPage';
import HubspotChat from 'routes/App/HubspotChat';
import EmailMarketing from 'routes/App/emailMarketing';
import EmailMarketingV2 from 'routes/App/emailMarketingV2';
import InsightsRoutes from 'routes/App/Insights';
import MarketingIntegrations from 'routes/App/MarketingIntegrations';
import Help from 'routes/App/Help';
import Flyers from 'routes/App/Flyers';
import StoreAppearance from 'routes/App/StoreAppearance';
import MainPage from 'routes/App/MainPage';
import Cashback from 'routes/App/cashback';
import InviteABite from 'routes/App/inviteABite';
import ByocOnboardingRoutes from './byocOnboarding';

const OnlineRoutes = () => {
  const status = useSelector((state) => state.user.userData?.status);
  const restrictions = useSelector(
    (state) => state.user.userData?.restrictions
  );
  const allServiceFeatures = useSelector(
    (state) => state.user.activeLocation.AllowedServiceFeatures
  );
  const serviceFeatures = allServiceFeatures.filter(
    (feature) => !(restrictions || []).includes(feature)
  );
  const isComoLocation = useSelector(
    (state) => state.user.activeLocation?.IsComoLocation
  );
  const isByocOnly = useSelector(
    (state) =>
      state.user.activeLocation?.IsByocOnly === true ||
      state.user.activeLocation?.isByocOnly === true
  );
  const user = useSelector((state) => state.user);
  const location = useLocation();

  return (
    <>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.MAIN_PAGE]}
      >
        <Route exact path='/main-page' component={MainPage} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={['analyticsAccess']}
        features={[FEATURE_RESTRICTION_IDS.ANALYTICS]}
      >
        <Route exact path='/analytics' component={Dashboard} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.REPORTS]}
      >
        <Route exact path='/reports' component={ReportsContainer} />
      </RestrictedArea>
      <RestrictedArea restrictions={['store_appearance']} features={[]}>
        <Route exact path='/store-appearance' component={StoreAppearance} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.STORE_SETTINGS]}
      >
        <Route exact path='/store-settings' component={Settings} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[getInsightsRestriction(serviceFeatures)]}
      >
        <Route path='/insights' component={InsightsRoutes} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.EMAIL_MARKETING]}
        extraAccessLogic={() => !isComoLocation}
      >
        <Route
          exact
          path='/marketing'
          render={() =>
            serviceFeatures.includes(
              FEATURE_RESTRICTION_IDS.EMAIL_MARKETING_DASHBOARD_V2
            ) ? (
              <Redirect to='/marketing-v2' />
            ) : (
              <EmailMarketing />
            )
          }
        />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[
          FEATURE_RESTRICTION_IDS.EMAIL_MARKETING,
          FEATURE_RESTRICTION_IDS.EMAIL_MARKETING_DASHBOARD_V2
        ]}
        extraAccessLogic={() => !isComoLocation}
      >
        <Route path='/marketing-v2' component={EmailMarketingV2} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.FLYERS]}
      >
        <Route exact path='/flyers' component={Flyers} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={['virtualNumberAccess']}
        features={[FEATURE_RESTRICTION_IDS.VIRTUAL_NUMBERS]}
        extraAccessLogic={() => Boolean(user.activeLocation?.GeneratedLinkURL)}
      >
        <Route exact path='/virtual-number' component={VirtualNumber} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.FEEDBACK]}
      >
        <Route exact path='/feedback' component={FeedbackRoutes} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.CUSTOMER_INSIGHTS]}
        extraAccessLogic={() => !isByocOnly}
      >
        <Route exact path='/customer-insights' component={CustomerInsights} />
      </RestrictedArea>
      {/* <RestrictedArea restrictions={[]} features={[FEATURE_RESTRICTION_IDS.FEEDBACKS]}> */}
      {/*   <Route exact path='/reviews' component={Reviews} /> */}
      {/* </RestrictedArea> */}
      <RestrictedArea
        restrictions={[]}
        features={
          checkIfSauceEmployee()
            ? []
            : [getBookADriverRestriction(serviceFeatures)].filter(Boolean)
        }
        extraAccessLogic={() => hasBookADriverAccess(serviceFeatures || [])}
      >
        <Route path='/book-a-driver' component={BookADriverRoutes} />
      </RestrictedArea>
      <Route path='/merchants' component={MerchantsRoutes} />
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.DISCOUNTS]}
        extraAccessLogic={() => !isComoLocation}
      >
        <Route path='/discounts' component={Discounts} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.DISCOUNTS_V2]}
        extraAccessLogic={() => !isComoLocation}
      >
        <Route path='/discounts-v2' component={DiscountsV2} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.BYOC_ONBOARDING]}
      >
        <Route path='/byoc-onboarding' component={ByocOnboardingRoutes} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.CASHBACK]}
        extraAccessLogic={() => !isComoLocation}
      >
        <Route exact path='/cashback' component={Cashback} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.INVITE_A_BITE]}
      >
        <Route exact path='/invite-a-bite' component={InviteABite} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.TEAM]}
      >
        <Route exact path='/team' component={TeamContainer} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.HELP]}
      >
        <Route exact path='/help' component={Help} />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.MARKETING_INTEGRATIONS]}
      >
        <Route
          path='/marketing-integrations'
          component={MarketingIntegrations}
        />
      </RestrictedArea>
      <RestrictedArea
        restrictions={[]}
        features={[FEATURE_RESTRICTION_IDS.MY_ACCOUNT]}
      >
        <Route path='/my-account' component={AccountRoutes} />
        <Route
          exact
          path='/my-account/create-landing'
          component={CreateLanding}
        />
        <Route
          exact
          path='/my-account/verify-your-bank-account'
          component={BankAccount}
        />
        <Route exact path='/my-account/team-members' component={TeamMembers} />
        <Route exact path='/my-account/add-a-link' component={AddLink} />
      </RestrictedArea>
      <Route
        exact
        path='/google-my-business-redirect'
        component={GoogleMyBusiness}
      />
      <Route
        path='/login_token'
        render={(p) => p.history.push('/my-account')}
      />

      <Route
        path='/'
        render={() => {
          let path;
          if (
            location.pathname === '/' &&
            status &&
            status === 'ACTIVE' &&
            serviceFeatures.includes(FEATURE_RESTRICTION_IDS.MAIN_PAGE)
          ) {
            path = '/main-page';
          } else if (
            location.pathname === '/' &&
            status &&
            status === 'ACTIVE' &&
            serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS)
          ) {
            path = '/orders';
          } else if (
            location.pathname === '/' &&
            status &&
            status === 'ACTIVE' &&
            (serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS_V2) ||
              serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.SHOW_BYOC_EVENTS
              ))
          ) {
            path = '/orders-v2';
          } else if (
            location.pathname === '/' &&
            status &&
            status === 'ACTIVE' &&
            (serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS_V2) ||
              serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.SHOW_BYOC_EVENTS
              ))
          ) {
            path = '/orders-v2';
          } else if (
            (location.pathname === '/orders-v2' ||
              location.pathname?.startsWith('/orders-v2')) &&
            status &&
            status === 'ACTIVE' &&
            (serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS_V2) ||
              serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.SHOW_BYOC_EVENTS
              ))
          ) {
            path = location.pathname;
          } else if (
            (location.pathname === '/orders' ||
              location.pathname?.startsWith('/orders/')) &&
            status &&
            status === 'ACTIVE' &&
            !serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS) &&
            (serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS_V2) ||
              serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.SHOW_BYOC_EVENTS
              ))
          ) {
            path = '/orders-v2';
          } else if (
            (location.pathname === '/orders-v2' ||
              location.pathname?.startsWith('/orders-v2/')) &&
            status &&
            status === 'ACTIVE' &&
            !serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS_V2) &&
            !serviceFeatures.includes(
              FEATURE_RESTRICTION_IDS.SHOW_BYOC_EVENTS
            ) &&
            serviceFeatures.includes(FEATURE_RESTRICTION_IDS.ORDERS)
          ) {
            path = '/orders';
          } else if (
            location.pathname.startsWith('/marketing-v2') &&
            serviceFeatures.includes(
              FEATURE_RESTRICTION_IDS.EMAIL_MARKETING_DASHBOARD_V2
            )
          ) {
            path = location.pathname;
          } else if (
            location.pathname.startsWith('/marketing-v2') &&
            serviceFeatures.includes(FEATURE_RESTRICTION_IDS.EMAIL_MARKETING)
          ) {
            path = '/marketing';
          } else if (
            location.pathname !== '/' &&
            status !== 'NEW' &&
            serviceFeatures.find(
              (feature) =>
                feature === location.pathname.split('-').join('_').split('/')[1]
            )
          ) {
            path = location.pathname;
          } else if (
            location.pathname.startsWith('/book-a-driver/') &&
            hasBookADriverAccess(serviceFeatures || [])
          ) {
            path = location.pathname;
          } else if (
            location.pathname.indexOf('/insights') === 0 &&
            !serviceFeatures.includes(
              FEATURE_RESTRICTION_IDS.INSIGHTS_GROWTH
            ) &&
            !serviceFeatures.includes(
              FEATURE_RESTRICTION_IDS.INSIGHTS_PERFORMANCE
            )
          ) {
            path = '/';
          } else if (
            (location.pathname.indexOf('/insights/growth') === 0 &&
              serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.INSIGHTS_GROWTH
              )) ||
            (location.pathname.indexOf('/insights/performance') === 0 &&
              serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.INSIGHTS_PERFORMANCE
              ))
          ) {
            path = location.pathname;
          } else if (
            (location.pathname.indexOf('/insights/growth') === 0 &&
              !serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.INSIGHTS_GROWTH
              )) ||
            (location.pathname.indexOf('/insights/performance') === 0 &&
              !serviceFeatures.includes(
                FEATURE_RESTRICTION_IDS.INSIGHTS_PERFORMANCE
              ))
          ) {
            path = '/insights';
          } else if (
            location.pathname.startsWith('/marketing-integrations/') &&
            serviceFeatures.includes(
              FEATURE_RESTRICTION_IDS.MARKETING_INTEGRATIONS
            )
          ) {
            path = location.pathname;
          } else if (
            location.pathname.startsWith('/store-appearance') &&
            !(restrictions || []).includes('store_appearance')
          ) {
            path = '/store-appearance';
          } else if (
            location.pathname === '/customer-insights' &&
            serviceFeatures.includes(FEATURE_RESTRICTION_IDS.CUSTOMER_INSIGHTS)
          ) {
            path = '/customer-insights';
          } else if (
            serviceFeatures.includes(FEATURE_RESTRICTION_IDS.MY_ACCOUNT)
          ) {
            path = '/my-account';
          } else {
            path =
              FEATURE_RESTRICTION_PATHS[
                serviceFeatures.find(
                  (feature) => FEATURE_RESTRICTION_PATHS[feature]
                )
              ];
          }

          if (!path) {
            // When serviceFeatures is empty, the sibling check below already
            // renders NoMatchPage — avoid rendering it twice.
            return serviceFeatures.length ? <NoMatchPage /> : null;
          }

          return (
            <Redirect
              to={
                path === location.pathname
                  ? {
                      pathname: path,
                      search: location.search,
                      hash: location.hash
                    }
                  : path
              }
            />
          );
        }}
      />
      {!serviceFeatures.length && <NoMatchPage />}
    </>
  );
};

export default OnlineRoutes;
